import { useState } from 'react';
import { Access, EmployeeType } from '@prisma/client';
import { Space, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';

import { isAgsEntitlementAccess, isAgsRoleAccess, SourceAccessItem } from '@app/domain-access-interfaces';
import { Picker, Tables } from '@app/web-components';

import { useFindBundleById } from '../../../../bundles';
import { BundlePicker } from '../../../../bundles/forms/inputs/bundle-picker';
import { EmployeeTypeRenderer, useAccessSelectTable } from '../../../atoms';

export type BundleSearchPanelProps = {
  disabled?: Tables.SelectTableDisabledType<SourceAccessItem | Access>;
  mode?: 'single' | 'multiple';
  value: (SourceAccessItem | Access)[];
  onChange?: (newValue: (SourceAccessItem | Access)[]) => void;
};

export const BundleSearchPanel = (props: BundleSearchPanelProps) => {
  const [bundle, setBundle] = useState<Picker.PickerOption | undefined>(undefined);
  const { data, isFetching } = useFindBundleById({ bundleId: Number(bundle?.value ?? -1), enabled: !!bundle });
  const { getProps } = useAccessSelectTable<Access>();

  const columns: ColumnsType<Access> = [
    {
      title: 'Source',
      dataIndex: 'accessSource',
      width: 170,
    },
    {
      title: 'Emp Type',
      dataIndex: 'employeeType',
      width: 100,
      render: (employeeType: EmployeeType) => <EmployeeTypeRenderer employeeType={employeeType} />,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (_, record) => {
        if (isAgsRoleAccess(record) || isAgsEntitlementAccess(record)) {
          return (
            <>
              <div>{record.name.replace(/ /g, '\u00a0')}</div>
              {record.name && record.meta?.displayName && record.name !== record.meta.displayName && (
                <Typography.Text type="secondary">
                  a.k.a. {record.meta.displayName.replace(/ /g, '\u00a0')}
                </Typography.Text>
              )}
            </>
          );
        }

        return record.name;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
  ];

  const dataSource = data?.accesses.map((bundleAccess) => bundleAccess.access);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <BundlePicker value={bundle} onChange={(value) => setBundle(value)} />
      <Tables.SelectTable
        {...getProps()}
        columns={columns}
        dataSource={dataSource}
        loading={isFetching}
        pagination={{
          total: dataSource?.length ?? 0,
          showTotal: (total) => (
            <div>
              <b>{total}</b>
              &nbsp; Records
            </div>
          ),
          showSizeChanger: false,
        }}
        scroll={{ y: 380 }}
        {...props}
      />
    </Space>
  );
};
